import { Avatar, Button, cn, LabelCaps } from '@pm/ui';
import { BellRing, LogOut, PanelLeftClose, PanelLeftOpen, Settings2 } from 'lucide-react';
import { NavLink } from 'react-router';
import { useAuth, useMe } from '@/lib/auth';
import { BrandMark } from '@/components/BrandMark';
import { navItemsFor, type NavItem } from './nav';

function Item({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          'relative flex h-8 items-center gap-2.5 rounded px-2 text-base text-text-2 transition-[background,color] duration-(--dur-fast) hover:bg-surface-2 hover:text-text-1 focus-visible:outline-none focus-visible:shadow-ring',
          isActive &&
            'bg-accent-soft text-accent hover:bg-accent-soft hover:text-accent before:absolute before:-left-2 before:top-[7px] before:bottom-[7px] before:w-0.5 before:rounded-[1px] before:bg-accent',
          collapsed && 'justify-center px-0',
        )
      }
    >
      <Icon className="size-4 shrink-0" aria-hidden />
      {!collapsed ? <span className="truncate">{item.label}</span> : null}
    </NavLink>
  );
}

export function Sidebar({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const me = useMe();
  const { logout } = useAuth();
  const items = navItemsFor(me);
  const main = items.filter((i) => i.group !== 'personal');
  const personal = items.filter((i) => i.group === 'personal');

  return (
    <aside className="glass sticky top-0 z-(--z-sidebar) flex h-screen flex-col gap-0.5 border-r border-glass-border p-2">
      <div
        className={cn(
          'flex items-center gap-2 px-2 pb-3 pt-1.5',
          collapsed && 'justify-center px-0',
        )}
      >
        <BrandMark collapsed={collapsed} />
      </div>

      <nav aria-label="Principal" className="flex flex-col gap-0.5">
        {main.map((item) => (
          <Item key={item.to} item={item} collapsed={collapsed} />
        ))}
      </nav>

      {personal.length > 0 ? (
        <>
          <div className="my-2.5 h-px bg-border" />
          {!collapsed ? <LabelCaps className="px-2 pb-1">Pessoal</LabelCaps> : null}
          <nav aria-label="Pessoal" className="flex flex-col gap-0.5">
            {personal.map((item) => (
              <Item key={item.to} item={item} collapsed={collapsed} />
            ))}
          </nav>
        </>
      ) : null}

      <div className="mt-auto flex flex-col gap-0.5">
        <NavLink
          to="/settings/appearance"
          title="Aparência"
          className={({ isActive }) =>
            cn(
              'flex h-8 items-center gap-2.5 rounded px-2 text-sm text-text-2 hover:bg-surface-2 hover:text-text-1 focus-visible:outline-none focus-visible:shadow-ring',
              isActive && 'bg-accent-soft text-accent',
              collapsed && 'justify-center px-0',
            )
          }
        >
          <Settings2 className="size-4 shrink-0" aria-hidden />
          {!collapsed ? 'Aparência' : null}
        </NavLink>
        <NavLink
          to="/settings/notifications"
          title="Notificações"
          className={({ isActive }) =>
            cn(
              'flex h-8 items-center gap-2.5 rounded px-2 text-sm text-text-2 hover:bg-surface-2 hover:text-text-1 focus-visible:outline-none focus-visible:shadow-ring',
              isActive && 'bg-accent-soft text-accent',
              collapsed && 'justify-center px-0',
            )
          }
        >
          <BellRing className="size-4 shrink-0" aria-hidden />
          {!collapsed ? 'Notificações' : null}
        </NavLink>
        <div
          className={cn('flex items-center gap-2 px-2 py-1.5', collapsed && 'justify-center px-0')}
        >
          <NavLink
            to="/settings/profile"
            title="Meu perfil"
            className={({ isActive }) =>
              cn(
                'flex min-w-0 flex-1 items-center gap-2 rounded text-sm text-text-2 hover:text-text-1 focus-visible:outline-none focus-visible:shadow-ring',
                isActive && 'text-accent',
                collapsed && 'flex-none',
              )
            }
          >
            <Avatar name={me.user.name} src={me.user.avatar_url} size="md" />
            {!collapsed ? <span className="min-w-0 truncate">{me.user.name}</span> : null}
          </NavLink>
          {!collapsed ? (
            <>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Sair"
                title="Sair"
                onClick={() => void logout()}
              >
                <LogOut />
              </Button>
            </>
          ) : null}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggle}
          aria-label={collapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}
          title={`${collapsed ? 'Expandir' : 'Colapsar'} ([)`}
          className={cn('justify-start', collapsed && 'justify-center')}
        >
          {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
          {!collapsed ? <span className="text-sm">Colapsar</span> : null}
        </Button>
      </div>
    </aside>
  );
}
